import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import './ProsodyReferencePage.css';

interface ProsodyCategory {
    key: string;
    display: [string, string];
    type:
        | 'word_stress' | 'sentence_stress' | 'intonation'
        | 'weak_forms' | 'connected_speech' | 'rhythm'
        // Acoustic delivery — surfaced from the recorded-speech
        // analysis (pace, pauses, voice projection) rather than
        // English-specific linguistic prosody. Lives in the same
        // reference page so coach tips can deep-link consistently.
        | 'delivery';
}

const CATEGORIES: ProsodyCategory[] = [
    // Word Stress
    { key: 'noun_verb', display: ['RE·cord', 're·CORD'], type: 'word_stress' },
    { key: 'compound', display: ['BLACK·bird', 'black BIRD'], type: 'word_stress' },
    { key: 'suffix_shift', display: ['PHO·to', 'pho·TO·graphy'], type: 'word_stress' },
    // Sentence Stress
    { key: 'content_function', display: ['實詞重讀', '虛詞弱讀'], type: 'sentence_stress' },
    { key: 'contrastive', display: ['平淡帶過', '加強對比'], type: 'sentence_stress' },
    // Intonation
    { key: 'rising_falling', display: ['Yes/No ↗', 'Wh- ↘'], type: 'intonation' },
    { key: 'lists', display: ['A↗ B↗', 'C↘'], type: 'intonation' },
    { key: 'tag_questions', display: ['sure ↘', 'unsure ↗'], type: 'intonation' },
    // Weak Forms
    { key: 'schwa', display: ['/ə/', 'about'], type: 'weak_forms' },
    { key: 'common_weak', display: ['/kæn/', '/kən/'], type: 'weak_forms' },
    // Connected Speech
    { key: 'contraction', display: ["do not", "don't"], type: 'connected_speech' },
    { key: 'linking', display: ['an apple', 'a‿napple'], type: 'connected_speech' },
    { key: 'elision', display: ['next day', "nex' day"], type: 'connected_speech' },
    { key: 'assimilation', display: ["don't you", 'dontchu'], type: 'connected_speech' },
    // Rhythm
    { key: 'stress_timed', display: ['重音抓拍', '字字等長'], type: 'rhythm' },
    // Delivery (acoustic, not phonological). Display tuples here
    // double as the card's at-a-glance title — keep them in the
    // learner's reading register, not English shorthand, because
    // these issues don't have an iconic native-English example
    // pair the way "RE·cord / re·CORD" works.
    { key: 'pace_fast', display: ['語速太快', '理想 ~3 字/秒'], type: 'delivery' },
    { key: 'pace_slow', display: ['字字分明', '節奏感喪失'], type: 'delivery' },
    { key: 'hesitant_pauses', display: ['停頓太多', '一氣呵成'], type: 'delivery' },
    { key: 'voice_projection', display: ['聲音偏小', '飽滿有力'], type: 'delivery' },
];

const TYPE_ORDER: ProsodyCategory['type'][] = [
    'word_stress', 'sentence_stress', 'intonation', 'weak_forms', 'connected_speech', 'rhythm', 'delivery',
];

/** Parse comma-separated examples into individual items */
function parseExamples(raw: string): string[] {
    return raw.split(',').map(s => s.trim()).filter(Boolean);
}

// ─── Collapsible examples ───

const ExamplesSection: React.FC<{ examples: string; label: string }> = ({ examples, label }) => {
    const [open, setOpen] = useState(false);
    const items = parseExamples(examples);

    return (
        <div className="prosody-ref__examples-section">
            <button
                className={`prosody-ref__examples-toggle ${open ? 'prosody-ref__examples-toggle--open' : ''}`}
                onClick={() => setOpen(prev => !prev)}
                type="button"
            >
                <svg className="prosody-ref__examples-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                </svg>
                <span>{label}</span>
                <span className="prosody-ref__examples-count">{items.length}</span>
            </button>

            {open && (
                <div className="prosody-ref__examples-body">
                    {items.map((item, idx) => (
                        <span key={idx} className="prosody-ref__example-item">{item}</span>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Main page ───

const ProsodyReferencePage: React.FC = () => {
    const { t } = useTranslation('prosodyRef');
    const location = useLocation();

    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }
        }
    }, [location.hash]);

    const grouped = TYPE_ORDER.map(type => ({
        type,
        items: CATEGORIES.filter(c => c.type === type),
    })).filter(g => g.items.length > 0);

    return (
        <div className="prosody-ref">
            <div className="prosody-ref__header">
                <Link to="/knowledge-graph" className="prosody-ref__back">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                    </svg>
                    {t('backToGraph', 'Knowledge Graph')}
                </Link>
                <h1 className="prosody-ref__title">{t('title', 'Prosody Guide')}</h1>
                <p className="prosody-ref__subtitle">{t('subtitle', 'Stress, intonation, and rhythm — the music of English')}</p>
            </div>

            <div className="prosody-ref__intro">
                <p>{t('intro')}</p>
            </div>

            {/* Table of contents */}
            <nav className="prosody-ref__toc">
                <h2 className="prosody-ref__toc-title">{t('toc', 'Categories')}</h2>
                <div className="prosody-ref__toc-grid">
                    {grouped.map(g => (
                        <div key={g.type} className="prosody-ref__toc-group">
                            <span className="prosody-ref__toc-type">{t(`type.${g.type}`)}</span>
                            <div className="prosody-ref__toc-links">
                                {g.items.map(item => (
                                    <a key={item.key} href={`#${item.key}`} className="prosody-ref__toc-link">
                                        {item.display[0]} / {item.display[1]}
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </nav>

            {/* Category sections */}
            {grouped.map(g => (
                <div key={g.type} id={g.type} className="prosody-ref__group">
                    <h2 className="prosody-ref__group-title">{t(`type.${g.type}`)}</h2>
                    {g.items.map(item => (
                        <div key={item.key} id={item.key} className="prosody-ref__card">
                            <div className="prosody-ref__card-header">
                                <span className="prosody-ref__pair">
                                    <span className="prosody-ref__pattern">{item.display[0]}</span>
                                    <span className="prosody-ref__vs">vs</span>
                                    <span className="prosody-ref__pattern">{item.display[1]}</span>
                                </span>
                                <span className={`prosody-ref__type-badge prosody-ref__type-badge--${item.type}`}>
                                    {t(`type.${item.type}`)}
                                </span>
                            </div>

                            <div className="prosody-ref__card-body">
                                <div className="prosody-ref__section">
                                    <h4>{t('whyMatters', 'Why it matters')}</h4>
                                    <p>{t(`cats.${item.key}.why`)}</p>
                                </div>
                                <div className="prosody-ref__section">
                                    <h4>{t('keyRule', 'Key rule')}</h4>
                                    <p>{t(`cats.${item.key}.tip`)}</p>
                                </div>

                                <ExamplesSection
                                    examples={t(`cats.${item.key}.examples`)}
                                    label={t('examples', 'Examples')}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export default ProsodyReferencePage;
